import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import ParticlesAuth from "components/common/ParticlesAuth";
import { getDocumentTitle } from "utils/helpers";
import { forgotPassword } from "./services/AuthService";
import logoSm from "assets/images/logo-sm.png";

const forgotSchema = Yup.object({
  email: Yup.string()
    .trim()
    .email("Enter a valid email")
    .required("Email is required"),
});

const DEFAULT_ACK =
  "If an account exists for that email, you will receive password reset instructions.";

const ForgotPassword = () => {
  const [done, setDone] = useState(false);
  const [ackMessage, setAckMessage] = useState(DEFAULT_ACK);

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: forgotSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        const res = await forgotPassword({ email: values.email.trim() });
        const msg =
          typeof res.message === "string" && res.message.trim()
            ? res.message
            : DEFAULT_ACK;
        setAckMessage(msg);
        setDone(true);
      } catch {
        toast.error("Something went wrong. Please try again later.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  document.title = getDocumentTitle("Forgot password");

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content">
          <Container>
            <Row className="justify-content-center min-vh-100 align-items-center py-5">
              <Col xs={12}>
                <div className="text-center mb-4">
                  <div className="mb-2">
                    <img src={logoSm} alt="Automation Engineers" height="52" />
                  </div>
                  <h4 className="fw-bold text-dark mb-1">AUTOMATION ENGINEERS</h4>
                  <p className="text-muted mb-0">Enter your email to receive a password reset link</p>
                </div>
              </Col>

              <Col md={8} lg={6} xl={5}>
                <Card>
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Forgot password?</h5>
                      <p className="text-muted">
                        Enter your email and we will send reset instructions if
                        an account exists.
                      </p>
                    </div>
                    <div className="p-2 mt-4">
                      {done ? (
                        <Alert color="success" className="mb-0">
                          {ackMessage}
                        </Alert>
                      ) : (
                        <Form onSubmit={formik.handleSubmit}>
                          <div className="mb-3">
                            <Label htmlFor="fp-email" className="form-label">
                              Email
                            </Label>
                            <Input
                              id="fp-email"
                              name="email"
                              type="email"
                              autoComplete="email"
                              placeholder="Enter your email"
                              value={formik.values.email}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              invalid={
                                !!(formik.touched.email && formik.errors.email)
                              }
                            />
                            {formik.touched.email && formik.errors.email ? (
                              <FormFeedback type="invalid">
                                {formik.errors.email}
                              </FormFeedback>
                            ) : null}
                          </div>
                          <div className="mt-4">
                            <Button
                              color="primary"
                              className="w-100"
                              type="submit"
                              disabled={formik.isSubmitting}
                            >
                              {formik.isSubmitting ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  Sending…
                                </>
                              ) : (
                                "Send reset link"
                              )}
                            </Button>
                          </div>
                        </Form>
                      )}
                      <div className="mt-4 text-center">
                        <Link to="/login" className="text-muted">
                          <i className="mdi mdi-chevron-left" /> Back to sign in
                        </Link>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default ForgotPassword;
